import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  History,
  BarChart3,
  TestTube,
  Archive,
  Trash2,
  Copy,
  Download,
  Upload,
} from "lucide-react";
import { format } from "date-fns";

interface Template {
  id: string;
  template_name: string;
  category: string;
  status: string;
  current_version: number;
  created_at: string;
  updated_at: string;
  test_status: string;
}

export default function AdminTemplateLibrary() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [searchQuery, statusFilter, templates]);

  async function loadTemplates() {
    try {
      const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  function filterTemplates() {
    let filtered = templates;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(t =>
        t.template_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    setFilteredTemplates(filtered);
  }

  function getStatusColor(status: string) {
    const colors = {
      published: 'bg-green-500',
      draft: 'bg-yellow-500',
      deprecated: 'bg-gray-500',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500';
  }

  function getTestStatusColor(status: string) {
    const colors = {
      passed: 'bg-green-500',
      failed: 'bg-red-500',
      pending: 'bg-yellow-500',
      not_tested: 'bg-gray-500',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500';
  }

  async function handleDeprecate(templateId: string) {
    if (!confirm('Are you sure you want to deprecate this template?')) return;

    try {
      const { error } = await supabase
        .from('document_templates')
        .update({
          status: 'deprecated',
          deprecated_at: new Date().toISOString(),
        })
        .eq('id', templateId);

      if (error) throw error;

      toast({
        title: "Template Deprecated",
        description: "Template has been marked as deprecated",
      });

      await loadTemplates();
    } catch (error: any) {
      console.error('Error deprecating template:', error);
      toast({
        title: "Error",
        description: "Failed to deprecate template",
        variant: "destructive",
      });
    }
  }

  async function handleDelete(templateId: string) {
    if (!confirm('Are you sure you want to delete this template? This action cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('document_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      toast({
        title: "Template Deleted",
        description: "Template has been permanently deleted",
      });

      await loadTemplates();
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    }
  }

  async function handleDuplicate(templateId: string) {
    try {
      const { data: template, error: fetchError } = await supabase
        .from('document_templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (fetchError || !template) return;

      const { data: { user } } = await supabase.auth.getUser();
      const { id, created_at, updated_at, ...templateData } = template;
      
      const { error } = await supabase
        .from('document_templates')
        .insert({
          ...templateData,
          template_name: `${template.template_name} (Copy)`,
          status: 'draft',
          current_version: 1,
          created_by: user?.id,
        });

      if (error) throw error;

      toast({
        title: "Template Duplicated",
        description: "Template has been duplicated as a draft",
      });

      await loadTemplates();
    } catch (error: any) {
      console.error('Error duplicating template:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate template",
        variant: "destructive",
      });
    }
  }

  async function handleExport(templateId: string) {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) return;

      const dataStr = JSON.stringify(template, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.template_name.replace(/\s+/g, '_')}_template.json`;
      link.click();
      URL.revokeObjectURL(url);

      toast({
        title: "Template Exported",
        description: "Template has been exported as JSON",
      });
    } catch (error: any) {
      console.error('Error exporting template:', error);
      toast({
        title: "Error",
        description: "Failed to export template",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Template Library</h1>
          <p className="text-muted-foreground">Manage all document templates</p>
        </div>
        <Button onClick={() => navigate('/admin/templates/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === "all" ? "default" : "outline"}
            onClick={() => setStatusFilter("all")}
            size="sm"
          >
            All
          </Button>
          <Button
            variant={statusFilter === "published" ? "default" : "outline"}
            onClick={() => setStatusFilter("published")}
            size="sm"
          >
            Published
          </Button>
          <Button
            variant={statusFilter === "draft" ? "default" : "outline"}
            onClick={() => setStatusFilter("draft")}
            size="sm"
          >
            Draft
          </Button>
          <Button
            variant={statusFilter === "deprecated" ? "default" : "outline"}
            onClick={() => setStatusFilter("deprecated")}
            size="sm"
          >
            Deprecated
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Test Status</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Loading templates...
                </TableCell>
              </TableRow>
            ) : filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No templates found
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">{template.template_name}</TableCell>
                  <TableCell>{template.category}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(template.status)}>
                      {template.status}
                    </Badge>
                  </TableCell>
                  <TableCell>v{template.current_version}</TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline"
                      className={getTestStatusColor(template.test_status || 'not_tested')}
                    >
                      {template.test_status || 'not tested'}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(new Date(template.updated_at), 'PP')}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/templates/${template.id}/edit`)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/admin/templates/${template.id}/history`)}>
                          <History className="h-4 w-4 mr-2" />
                          Version History
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/admin/templates/${template.id}/analytics`)}>
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Analytics
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <TestTube className="h-4 w-4 mr-2" />
                          Test Template
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(template.id)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport(template.id)}>
                          <Download className="h-4 w-4 mr-2" />
                          Export JSON
                        </DropdownMenuItem>
                        {template.status !== 'deprecated' && (
                          <DropdownMenuItem onClick={() => handleDeprecate(template.id)}>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
